import { useState } from "react";
import { useReducedMotion } from "framer-motion";
import { Pause } from "lucide-react";

export const PhoneMockup = ({ src, alt, className = "" }) => (
  <div
    className={`relative mx-auto aspect-[9/19] w-[150px] rounded-[1.8rem] border-[5px] border-ink bg-ink shadow-[0_18px_40px_-12px_rgba(0,0,0,0.35)] ${className}`}
  >
    <div className="absolute left-1/2 top-1.5 z-10 h-1.5 w-14 -translate-x-1/2 rounded-full bg-ink/80" />
    <div className="h-full w-full overflow-hidden rounded-[1.4rem] bg-white">
      <img src={src} alt={alt} className="h-full w-full object-cover object-top" />
    </div>
  </div>
);

export const VideoPhoneMockup = ({ src, poster, className = "" }) => (
  <div
    data-testid="video-phone-mockup"
    className={`relative mx-auto aspect-[9/19] w-[150px] rounded-[1.8rem] border-[5px] border-ink bg-ink shadow-[0_18px_40px_-12px_rgba(0,0,0,0.35)] ${className}`}
  >
    <div className="absolute left-1/2 top-1.5 z-10 h-1.5 w-14 -translate-x-1/2 rounded-full bg-ink/80" />
    <div className="h-full w-full overflow-hidden rounded-[1.4rem] bg-black">
      <video
        className="h-full w-full object-cover object-top"
        src={src}
        poster={poster}
        autoPlay
        loop
        muted
        playsInline
        preload="metadata"
      />
    </div>
  </div>
);


// Phone frame whose screen auto-scrolls a tall stitched screenshot on a seamless loop.
// Hover (desktop) or tap (touch) pauses the scroll in place so visitors can read a section.
export const ScrollPhoneMockup = ({ src, alt, className = "", duration = 34 }) => {
  const reduce = useReducedMotion();
  const [hovering, setHovering] = useState(false);
  const [pinned, setPinned] = useState(false);
  const paused = hovering || pinned;

  return (
    <div
      data-testid="scan-scroll-mockup"
      className={`relative mx-auto aspect-[9/19] w-[150px] cursor-pointer rounded-[1.8rem] border-[5px] border-ink bg-ink shadow-[0_18px_40px_-12px_rgba(0,0,0,0.35)] ${className}`}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      onClick={(e) => {
        e.stopPropagation();
        setPinned((p) => !p);
      }}
      role="button"
      tabIndex={0}
      aria-label={`${alt} — tap to ${paused ? "resume" : "pause"} scrolling`}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          e.stopPropagation();
          setPinned((p) => !p);
        }
      }}
    >
      <div className="absolute left-1/2 top-1.5 z-10 h-1.5 w-14 -translate-x-1/2 rounded-full bg-ink/80" />
      <div
        className="relative h-full w-full overflow-hidden rounded-[1.4rem]"
        style={{ backgroundColor: "#E7F3FF" }}
      >
        <div
          data-testid="scan-scroll-track"
          className="will-change-transform"
          style={
            reduce
              ? undefined
              : {
                  animation: `scan-scroll ${duration}s linear infinite`,
                  animationPlayState: paused ? "paused" : "running",
                }
          }
        >
          <img src={src} alt={alt} className="block w-full select-none" draggable="false" />
          <img src={src} alt="" aria-hidden="true" className="block w-full select-none" draggable="false" />
        </div>
        {/* subtle top/bottom fade so content eases in/out of the screen */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-6 bg-gradient-to-b from-[#E7F3FF] to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-6 bg-gradient-to-t from-[#E7F3FF] to-transparent" />
        {/* pause affordance */}
        <div
          className={`pointer-events-none absolute inset-0 z-10 flex items-center justify-center transition-opacity duration-300 ${
            paused && !reduce ? "opacity-100" : "opacity-0"
          }`}
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-ink/70 backdrop-blur-sm">
            <Pause className="h-4 w-4 text-white" strokeWidth={2} fill="currentColor" />
          </span>
        </div>
      </div>
    </div>
  );
};
